import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { onMutateCaseStatus } from 'src/components/organismsLegacy/CreateReportHeader/useCaseStatus';
import {
  CaseByIdQuery,
  CaseType,
  CoiCaseStatus,
  ReportStatus,
  ReportType,
  UserType,
  useAddCaseCategoryMutation,
  useCaseByIdQuery,
  useDisclosureCaseByIdQuery,
  useGetActiveCategoriesForCaseQuery,
  useGetCaseLinksByCaseIdQuery,
  useMeQuery,
  useReassignReportAssigneeMutation,
  useRelationshipTypeQuery,
  useRemoveCaseCategoryMutation,
  useUpdateCaseStatusMutation,
  useUpdateCoiCaseStatusMutation,
  useUpdateDeterminedCategoryMutation,
  useUpdateSubstantiationStatusMutation,
} from 'src/graphql';

export const useCase = (id: string, translation?: string) => {
  const queryClient = useQueryClient();

  // Check if there are pending uploads to determine if we need polling
  const shouldPoll = (data: CaseByIdQuery | undefined): boolean => {
    if (!data?.caseById?.CaseDocuments) return false;
    const pendingUploads = data.caseById.CaseDocuments.flatMap(
      (doc) =>
        doc.Uploads?.filter((upload) => upload.scanStatus === 'PENDING') || [],
    );
    if (pendingUploads.length > 0) return true;
    return false;
  };

  const {
    data: caseData,
    isLoading,
    isError,
    refetch,
  } = useCaseByIdQuery(
    {
      id,
      translation,
    },
    {
      // Dynamic polling: only poll when there are pending uploads
      refetchInterval: (data) => {
        return shouldPoll(data) ? 15000 : false;
      },
      refetchIntervalInBackground: false, // Don't poll when tab is not active
    },
  );

  const { data: viewerData } = useMeQuery();
  const { data: availableCategories } = useGetActiveCategoriesForCaseQuery();
  const { data: relationshipTypes } = useRelationshipTypeQuery();
  const { data: caseLinks } = useGetCaseLinksByCaseIdQuery({
    caseId: id,
  });
  const [isSaving, setIsSaving] = useState(false);

  const caseById: CaseType = caseData?.caseById as CaseType;
  const viewer: UserType = viewerData?.me! as UserType;
  const qaResponseId = caseById?.DisclosuresCaseData?.responseId;

  const {
    data: qaData,
    isLoading: isQaLoading,
    isError: isQaError,
  } = useDisclosureCaseByIdQuery(
    { caseId: id },
    {
      enabled: Boolean(id) && Boolean(qaResponseId),
      refetchIntervalInBackground: false, // Don't poll when tab is not active
    },
  );
  const addCaseCategory = useAddCaseCategoryMutation({
    onError: () => {
      throw new Error('Failed to add Category');
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['CaseById', { id }]);
    },
  });

  const removeCaseCategory = useRemoveCaseCategoryMutation({
    onError: () => {
      throw new Error('Failed to remove Category');
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['CaseById', { id }]);
    },
  });
  const updateSubstantiationStatus = useUpdateSubstantiationStatusMutation({
    onError: () => {
      throw new Error('Error updating substantiation status');
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['CaseById', { id }]);
    },
  });

  const reassignReportAssignee = useReassignReportAssigneeMutation({
    onError: () => {
      throw new Error('Error reassigning report assignee');
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['CaseById', { id }]);
    },
  });

  const updateDeterminedCategory = useUpdateDeterminedCategoryMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['CaseById', { id }]);
    },
    onError: () => {
      throw new Error('Error updating determined category');
    },
  });

  const statusUpdatedRef = useRef(false);

  const updateCaseStatus = useUpdateCaseStatusMutation({
    onError: () => {
      throw new Error('Unable to update status');
    },
    onMutate: onMutateCaseStatus({ id, queryClient }),
    onSettled: () => {
      queryClient.invalidateQueries(['CaseById', { id }]);
    },
  });

  const updateCoiCaseStatus = useUpdateCoiCaseStatusMutation({
    onError: () => {
      throw new Error('Unable to update COI case status');
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['CaseById'] });
    },
  });

  const handleUpdateManagerClick = async (
    assigneeId: number,
    report: ReportType,
  ) => {
    await reassignReportAssignee.mutate({
      caseId: id,
      report: report.id,
      newReportAssigneeId: assigneeId,
    });
  };

  useEffect(() => {
    if (
      caseData?.caseById?.status === ReportStatus.New &&
      !statusUpdatedRef.current
    ) {
      const isCoi =
        caseData?.caseById?.DisclosuresCaseData?.importType === 'coi';

      if (isCoi) {
        if (!updateCoiCaseStatus.isLoading) {
          updateCoiCaseStatus.mutate({
            caseId: caseData.caseById.id,
            newCoiCaseStatus: CoiCaseStatus.UnderReview,
          });
        }
      } else {
        statusUpdatedRef.current = true;
        updateCaseStatus.mutate({
          newCaseStatus: ReportStatus.Read,
          caseId: caseData.caseById.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.caseById?.status]);

  const availableCategoriesList = useMemo(
    () => availableCategories?.getActiveCategoriesForCase ?? [],
    [availableCategories],
  );
  const relationshipTypesList = useMemo(
    () => relationshipTypes?.getRelationshipType ?? [],
    [relationshipTypes],
  );
  const caseLinksList = useMemo(
    () => caseLinks?.getCaseLinksByCaseId ?? [],
    [caseLinks],
  );
  const availableCaseManagers = useMemo(
    () =>
      caseById?.AllCaseLeads?.map((manager) => ({
        label: manager.name ?? 'Unnamed User',
        value: manager.id,
      })) ?? [],
    [caseById?.AllCaseLeads],
  );

  return {
    caseById,
    isLoading,
    isError,
    refetch,
    viewer,
    availableCategories: availableCategoriesList,
    relationshipTypes: relationshipTypesList,
    updateSubstantiationStatus,
    updateDeterminedCategory,
    handleUpdateManagerClick,
    isSaving,
    setIsSaving,
    addCaseCategory,
    removeCaseCategory,
    availableCaseManagers,
    caseLinks: caseLinksList,
    qaService: {
      qaResponseId,
      qaData,
      isQaLoading,
      isQaError,
    },
  };
};
